import React, { cloneElement, isValidElement } from 'react';

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const boldWordsInNodes = (
    nodes: React.ReactNode[],
    words: string[]
): React.ReactNode[] => {
    if (!words.length) return nodes;

    const pattern = new RegExp(
        `\\b(${words.map(escapeRegExp).join('|')})\\b`,
        'gi'
    );
    const out: React.ReactNode[] = [];

    nodes.forEach((node, nodeIdx) => {
        if (typeof node === 'string') {
            const parts = node.split(pattern);
            if (parts.length === 1) {
                out.push(node);
            } else {
                parts.forEach((part, i) => {
                    if (!part) return;
                    if (i % 2 === 1) {
                        out.push(
                            <strong key={`b-${nodeIdx}-${i}`}>{part}</strong>
                        );
                    } else {
                        out.push(part);
                    }
                });
            }
        } else if (isValidElement(node)) {
            const children = node.props.children;
            let newChildren = children;

            if (typeof children === 'string') {
                newChildren = boldWordsInNodes([children], words);
            } else if (Array.isArray(children)) {
                newChildren = boldWordsInNodes(children, words);
            }

            out.push(
                cloneElement(
                    node,
                    { key: node.key ?? `k-${nodeIdx}` },
                    newChildren
                )
            );
        } else {
            out.push(node);
        }
    });

    return out;
};

const insertLinks = (
    text: string,
    boldWords: string[] = ['irregular']
): React.ReactNode => {
    if (!text) return null;

    const replacements = [
        {
            phrase: 'Guidance: Hybrid working declaration',
            url: 'https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015366',
        },
        {
            phrase: 'Hybrid working policy',
            url: 'https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015021',
        },
        {
            phrase: 'UK remote working policy',
            url: 'https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015439',
        },
    ];

    let segments: React.ReactNode[] = [text];

    replacements.forEach(({ phrase, url }) => {
        const newSegments: React.ReactNode[] = [];

        segments.forEach((segment) => {
            if (typeof segment !== 'string') {
                newSegments.push(segment);
                return;
            }

            const parts = segment.split(phrase);

            if (parts.length === 1) {
                newSegments.push(segment);
                return;
            }

            for (let i = 0; i < parts.length; i++) {
                if (parts[i]) newSegments.push(parts[i]);
                if (i < parts.length - 1) {
                    newSegments.push(
                        <a
                            key={`${phrase}-${i}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-background-primary)' }}
                        >
                            {phrase}
                        </a>
                    );
                }
            }
        });

        segments = newSegments;
    });

    // Bold any target words (e.g., 'irregular') in the final node list
    segments = boldWordsInNodes(segments, boldWords);

    return <>{segments}</>;
};

export const renderFurtherInformation = (
    text: string,
    reasonForAbsence?: string
) => {
    if (!text) return null;

    const shouldUnderline =
        reasonForAbsence === 'Training / Professional event / Volunteering old';

    const parts = text.split(/Important:/);
    if (parts.length === 1) {
        const content = insertLinks(text, ['irregular']);
        return shouldUnderline ? (
            <span style={{ textDecoration: 'underline' }}>{content}</span>
        ) : (
            <span>{content}</span>
        );
    }

    const contentAfterImportant = parts[1].trim();

    if (contentAfterImportant.includes('- ')) {
        const bullets = contentAfterImportant
            .split(/- /)
            .map((str) => str.trim())
            .filter(Boolean);

        return (
            <div style={shouldUnderline ? { textDecoration: 'underline' } : {}}>
                <div>
                    <strong>Important: </strong>
                </div>
                <ul>
                    {bullets.map((point, idx) => (
                        <li key={idx}>{insertLinks(point, ['irregular'])}</li>
                    ))}
                </ul>
            </div>
        );
    } else {
        return (
            <div style={shouldUnderline ? { textDecoration: 'underline' } : {}}>
                <strong>Important: </strong>
                {insertLinks(contentAfterImportant, ['irregular'])}
            </div>
        );
    }
};
